import { useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";

import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { inferCarrierFromTrackingNumber } from "@/lib/inferCarrierFromTracking";
import { formatTimestamp } from "@/lib/utils";

const PAGE_HELP =
  "Track packages you ordered that are not on a purchase order and do not need a hub SKU. "
  + "Describe the item, add tracking, and optional handoff notes. You are notified when receiving logs the shipment at the dock. "
  + "Expected watches also appear on Shipping Arrivals before they land.";

const PACKAGE_ARRIVALS_QUERY_KEY = ["personal", "package-arrivals", "mine"] as const;

export type PersonalPackageWatchStatus = "expected" | "arrived" | "cancelled";

export interface PersonalPackageWatchRow {
  id: number;
  title: string;
  cpn: string | null;
  source: string | null;
  notes: string | null;
  tracking_number: string;
  tracking_carrier: string | null;
  expected_arrival: string | null;
  status: PersonalPackageWatchStatus;
  inbound_arrival_id: number | null;
  created_by_user_id: string;
  arrived_at: string | null;
  created_at: string;
  updated_at: string;
}

function PackageWatchStatusBadge({ status }: { status: PersonalPackageWatchStatus }): JSX.Element {
  if (status === "arrived") {
    return <Badge tone="success">Arrived</Badge>;
  }
  if (status === "cancelled") {
    return <Badge tone="neutral">Cancelled</Badge>;
  }
  return <Badge tone="warning">Expected</Badge>;
}

function formatExpectedArrival(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "No expected date";
  }
  return `Expected ${value}`;
}

function PackageArrivalListPage(): JSX.Element {
  const navigate = useNavigate();
  const listQuery = useQuery({
    queryKey: PACKAGE_ARRIVALS_QUERY_KEY,
    queryFn: () => apiFetch<{ rows: PersonalPackageWatchRow[] }>("/api/personal/package-arrivals/mine"),
  });
  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Package arrivals</PageTitleWithHelp>
        <Button type="button" onClick={() => navigate("/personal/package-arrivals/new")}>
          Track package
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Your watches</CardTitle>
        </CardHeader>
        <CardBody className="p-0 sm:p-0">
          {listQuery.isLoading ? (
            <p className="px-5 py-4 text-sm text-slate-500">Loading your package watches…</p>
          ) : listQuery.isError ? (
            <p className="px-5 py-4 text-sm text-red-600">{(listQuery.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-600">
              You are not watching any packages yet. Use Track package when you order something outside the normal PO flow.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/personal/package-arrivals/${encodeURIComponent(String(row.id))}`}
                    className="flex flex-col gap-2 px-5 py-4 transition-colors hover:bg-slate-50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">{row.title}</span>
                      <PackageWatchStatusBadge status={row.status} />
                    </div>
                    <p className="font-mono text-sm text-slate-700">{row.tracking_number}</p>
                    {row.notes?.trim() ? (
                      <p className="line-clamp-2 text-sm text-slate-600">{row.notes}</p>
                    ) : null}
                    <p className="text-xs text-slate-500">
                      {row.status === "arrived" && row.arrived_at
                        ? `Arrived ${formatTimestamp(row.arrived_at)}`
                        : formatExpectedArrival(row.expected_arrival)}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

type PackageWatchDraft = {
  title: string;
  cpn: string;
  source: string;
  notes: string;
  tracking_number: string;
  tracking_carrier: string;
  expected_arrival: string;
};

const EMPTY_DRAFT: PackageWatchDraft = {
  title: "",
  cpn: "",
  source: "",
  notes: "",
  tracking_number: "",
  tracking_carrier: "",
  expected_arrival: "",
};

function PackageArrivalFormPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PackageWatchDraft>(EMPTY_DRAFT);

  const createMutation = useMutation({
    mutationFn: (body: PackageWatchDraft) =>
      apiFetch<{ watch: PersonalPackageWatchRow }>("/api/personal/package-arrivals", {
        method: "POST",
        body: {
          title: body.title.trim(),
          cpn: body.cpn.trim() === "" ? null : body.cpn.trim(),
          source: body.source.trim() === "" ? null : body.source.trim(),
          notes: body.notes.trim() === "" ? null : body.notes.trim(),
          tracking_number: body.tracking_number.trim(),
          tracking_carrier: body.tracking_carrier.trim() === "" ? null : body.tracking_carrier.trim(),
          expected_arrival: body.expected_arrival.trim() === "" ? null : body.expected_arrival.trim(),
        },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: PACKAGE_ARRIVALS_QUERY_KEY });
      navigate(`/personal/package-arrivals/${encodeURIComponent(String(result.watch.id))}`);
    },
  });

  const canSubmit = draft.title.trim() !== "" && draft.tracking_number.trim() !== "";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>Track package</PageTitleWithHelp>
      <Card>
        <CardBody>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit) return;
              createMutation.mutate(draft);
            }}
          >
            <div>
              <Label htmlFor="package-watch-title">What is it?</Label>
              <Input
                id="package-watch-title"
                value={draft.title}
                onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                placeholder="Office chair, replacement monitor, vendor sample…"
              />
            </div>
            <div>
              <Label htmlFor="package-watch-cpn">SKU (optional)</Label>
              <AssemblySkuCombobox
                id="package-watch-cpn"
                value={draft.cpn}
                onChange={(next) => setDraft((current) => ({ ...current, cpn: next }))}
                placeholder="Leave blank when there is no hub SKU"
                className="w-full"
              />
            </div>
            <div>
              <Label htmlFor="package-watch-source">Source</Label>
              <Input
                id="package-watch-source"
                value={draft.source}
                onChange={(event) => setDraft((current) => ({ ...current, source: event.target.value }))}
                placeholder="Vendor, Amazon, internal transfer…"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="package-watch-tracking">Tracking number</Label>
                <Input
                  id="package-watch-tracking"
                  value={draft.tracking_number}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, tracking_number: event.target.value }))
                  }
                  onBlur={() => {
                    if (draft.tracking_carrier.trim() !== "") return;
                    const inferred = inferCarrierFromTrackingNumber(draft.tracking_number);
                    if (inferred != null) {
                      setDraft((current) => ({ ...current, tracking_carrier: inferred }));
                    }
                  }}
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="package-watch-carrier">Carrier</Label>
                <Input
                  id="package-watch-carrier"
                  value={draft.tracking_carrier}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, tracking_carrier: event.target.value }))
                  }
                  placeholder="Auto-filled from tracking when recognized"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="package-watch-expected">Expected arrival</Label>
              <Input
                id="package-watch-expected"
                type="date"
                value={draft.expected_arrival}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, expected_arrival: event.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="package-watch-notes">Handoff notes</Label>
              <Input
                id="package-watch-notes"
                value={draft.notes}
                onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Give to Eric, leave in office, job reference…"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" isLoading={createMutation.isPending} disabled={!canSubmit}>
                Start watching
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate("/personal/package-arrivals")}>
                Cancel
              </Button>
            </div>
            {createMutation.error ? (
              <p className="text-sm text-rose-700">{(createMutation.error as Error).message}</p>
            ) : null}
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function PackageArrivalDetailPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ["personal", "package-arrivals", "detail", id],
    queryFn: () => apiFetch<{ watch: PersonalPackageWatchRow }>(`/api/personal/package-arrivals/${encodeURIComponent(id)}`),
    enabled: id !== "",
  });
  const watch = detailQuery.data?.watch;

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ watch: PersonalPackageWatchRow }>(
        `/api/personal/package-arrivals/${encodeURIComponent(id)}/cancel`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKAGE_ARRIVALS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["personal", "package-arrivals", "detail", id] });
    },
  });

  if (detailQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading package watch…</p>;
  }
  if (detailQuery.isError || watch == null) {
    return <p className="text-sm text-rose-700">{(detailQuery.error as Error).message}</p>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>{watch.title}</PageTitleWithHelp>
        <div className="flex flex-wrap gap-2">
          {watch.status === "expected" ? (
            <Button
              type="button"
              variant="secondary"
              isLoading={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              Cancel watch
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={() => navigate("/personal/package-arrivals")}>
            Back to list
          </Button>
        </div>
      </header>

      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <PackageWatchStatusBadge status={watch.status} />
            {watch.inbound_arrival_id != null ? (
              <Link
                to={`/shipping/arrivals?arrival=${encodeURIComponent(String(watch.inbound_arrival_id))}`}
                className="inline-flex items-center gap-1 text-sm text-brand-700 hover:text-brand-800"
              >
                Open dock arrival
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </Link>
            ) : null}
          </div>
          <dl className="grid gap-3 text-sm">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Tracking</dt>
              <dd className="font-mono text-slate-900">{watch.tracking_number}</dd>
            </div>
            {watch.tracking_carrier?.trim() ? (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Carrier</dt>
                <dd className="text-slate-900">{watch.tracking_carrier}</dd>
              </div>
            ) : null}
            {watch.source?.trim() ? (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Source</dt>
                <dd className="text-slate-900">{watch.source}</dd>
              </div>
            ) : null}
            {watch.cpn?.trim() ? (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">SKU</dt>
                <dd className="font-mono text-slate-900">{watch.cpn}</dd>
              </div>
            ) : null}
            {watch.notes?.trim() ? (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Handoff notes</dt>
                <dd className="text-slate-900">{watch.notes}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Timeline</dt>
              <dd className="text-slate-900">
                Created {formatTimestamp(watch.created_at)}
                {watch.status === "arrived" && watch.arrived_at
                  ? ` · Arrived ${formatTimestamp(watch.arrived_at)}`
                  : watch.expected_arrival
                    ? ` · ${formatExpectedArrival(watch.expected_arrival)}`
                    : null}
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>
    </div>
  );
}

export function MyPackageArrivalsPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<PackageArrivalListPage />} />
      <Route path="new" element={<PackageArrivalFormPage />} />
      <Route path=":id" element={<PackageArrivalDetailPage />} />
      <Route path="*" element={<Navigate to="/personal/package-arrivals" replace />} />
    </Routes>
  );
}
